/**
 * Corretores.
 *
 * Reexportado por `../resources.ts`. Ver docs/FRONTEND.md, secao 3.
 */

import { query, type Address, type ListParams } from './comum';
import type { BrokerSusepScreening } from './susep';

import { api, requestPage } from '../client';

/**
 * Conta bancaria da corretora, destino do repasse como a chave Pix. Vem inteira
 * (banco, tipo, agencia e conta) ou toda vazia.
 */
export interface BankAccount {
  bank_code: string;
  account_type: string;
  agency: string;
  agency_digit: string;
  account: string;
  account_digit: string;
}

export const emptyBankAccount: BankAccount = {
  bank_code: '',
  account_type: '',
  agency: '',
  agency_digit: '',
  account: '',
  account_digit: '',
};

/** Responsavel financeiro da corretora. */
export interface FinancialContact {
  name: string;
  document: string;
  email: string;
  phone: string;
}

export const emptyFinancialContact: FinancialContact = {
  name: '',
  document: '',
  email: '',
  phone: '',
};

/** Situacao do CNPJ na Receita, como a do tomador. Vazio e nao informada. */
export type BrokerCnpjStatus =
  '' | 'ativa' | 'suspensa' | 'inapta' | 'baixada' | 'nula';

/**
 * Ficha "Dados cadastrais" do OnPoint no corretor (P-COR-1). Os campos sao opcionais
 * no tipo para que o corretor montado sem a ficha continue valido.
 */
export interface BrokerRecord {
  /** Limite de Comissao em pontos-base. Nulo e sem limite declarado. */
  max_commission_bps?: number | null;
  cnpj_status?: BrokerCnpjStatus;
  cnpj_status_checked_at?: string | null;
  cnae_code?: string;
  cnae_description?: string;
  cnae_branch?: string;
  legal_nature?: string;
  susep_registered_on?: string | null;
  reregistered_on?: string | null;
  registration_expires_on?: string | null;
  /** A expiracao ja passou. So leitura: nada e travado. */
  registration_expired?: boolean;
  susep_reregistration_due?: boolean;
  susep_status_checked_at?: string | null;
  compliance_pending?: boolean;
  address_city_code?: string;
  channel_type_id?: string | null;
  channel?: {
    id: string;
    name: string;
    description: string;
    requires_insurer_codes: boolean;
    active: boolean;
  } | null;
  insurer_broker_code?: string;
  insurer_branch_code?: string;
  insurer_contract_code?: string;
  level?: string;
  is_active?: boolean;
  is_blocked?: boolean;
  blocked_at?: string | null;
  block_reason?: string;
  person_kind?: string;
  administrator_member_id?: string | null;
  administrator?: { member_id: string; name: string; email: string } | null;
  document_unchecked?: boolean;
  imported?: boolean;
  has_pep_administrator?: boolean;
  has_pending_documents?: boolean;
  pending_documents_count?: number;

  /**
   * Corretor digital (P-COR-3). So muda pela ativacao; o que a marca libera ou
   * dispensa e regra sem fonte, e nada muda por ela.
   */
  is_digital?: boolean;
  digital_activated_at?: string | null;
  /** Dispensas da busca do OnPoint, guardadas sem efeito (P-COR-3). */
  ignore_compliance_validation?: boolean;
  ignore_pending_documents?: boolean;
}

export interface Broker extends BrokerRecord {
  id: string;
  number: number;
  legal_name: string;
  trade_name: string;
  document: string;
  susep_code: string;
  stage: string;
  next_stages: string[];
  external_status: string;
  email: string;
  phone: string;
  contact_name: string;
  address: Address;
  commission_bps: number;
  notes: string;
  pix_key: string;
  pix_kind: string;
  commercial_owner: string;
  product_ids: string[];
  municipal_registration: string;
  bank_account: BankAccount;
  financial_contact: FinancialContact;
  /**
   * Triagem da SUSEP (ADR 0095): a situacao e as habilitacoes da ultima consulta. O
   * numero fica em `susep_code`, o resultado em `external_status` e a data em
   * `susep_status_checked_at`.
   */
  susep_situation?: string;
  susep_products?: number[];
  susep_damages_enabled?: boolean;
  created_at: string;
  updated_at: string;
}

/** A ficha no pedido. O bloqueio nao entra: tem a transicao como caminho. */
export interface BrokerRecordInput {
  max_commission_bps?: number | null;
  cnpj_status?: BrokerCnpjStatus;
  cnae_code?: string;
  cnae_branch?: string;
  legal_nature?: string;
  /** Data AAAA-MM-DD. Texto vazio limpa. */
  susep_registered_on?: string;
  reregistered_on?: string;
  registration_expires_on?: string;
  susep_reregistration_due?: boolean;
  compliance_pending?: boolean;
  /** Nulo ou vazio tira o canal. */
  channel_type_id?: string | null;
  insurer_broker_code?: string;
  insurer_branch_code?: string;
  insurer_contract_code?: string;
  level?: string;
  /** Pessoa da propria corretora. Nulo ou vazio tira o administrador. */
  administrator_member_id?: string | null;
  /** Dispensas do OnPoint (P-COR-3), sem efeito. A marca digital nao entra aqui. */
  ignore_compliance_validation?: boolean;
  ignore_pending_documents?: boolean;
}

export interface BrokerInput extends BrokerRecordInput {
  legal_name?: string;
  trade_name?: string;
  document?: string;
  susep_code?: string;
  external_status?: string;
  email?: string;
  phone?: string;
  contact_name?: string;
  address?: Address;
  commission_bps?: number;
  notes?: string;
  pix_key?: string;
  pix_kind?: string;
  commercial_owner?: string;
  product_ids?: string[];
  municipal_registration?: string;
  /** Substitui a conta inteira. Ausente, a conta gravada fica como esta. */
  bank_account?: BankAccount;
  /** Substitui o responsavel inteiro. Ausente, fica como esta. */
  financial_contact?: FinancialContact;
}

// --- Listas da ficha (P-COR-1) -------------------------------------------------

export type BrokerPartnerRole = 'socio' | 'administrador' | 'representante';

export interface BrokerPartner {
  id: string;
  parent_id: string | null;
  role: BrokerPartnerRole;
  role_description: string;
  name: string;
  document: string;
  person_kind: 'pf' | 'pj';
  participation_bps: number | null;
  is_majority: boolean;
  is_pep: boolean;
  document_invalid: boolean;
  source: 'consulta' | 'digitado' | 'importado';
  created_label: string;
  created_at: string;
  updated_at: string;
}

export interface BrokerPartnerInput {
  parent_id?: string | null;
  role?: BrokerPartnerRole;
  role_description?: string;
  name?: string;
  document?: string;
  participation_bps?: number | null;
  is_majority?: boolean;
  is_pep?: boolean;
  document_invalid?: boolean;
}

export interface BrokerBankAccountEntry {
  id: string;
  bank_account: BankAccount;
  is_current: boolean;
  replaced_at: string | null;
  created_label: string;
  created_at: string;
}

export type BrokerResponsibleKind = 'financeiro' | 'tecnico' | 'outro';

export interface BrokerResponsible {
  id: string;
  kind: BrokerResponsibleKind;
  name: string;
  document: string;
  email: string;
  phone: string;
  created_label: string;
  created_at: string;
  updated_at: string;
}

export interface BrokerResponsibleInput {
  kind?: BrokerResponsibleKind;
  name?: string;
  document?: string;
  email?: string;
  phone?: string;
}

export interface BrokerAddress {
  id: string;
  zip_code: string;
  street: string;
  number: string;
  complement: string;
  district: string;
  city: string;
  state: string;
  city_code: string;
  international: boolean;
  position: number;
  is_main: boolean;
  active: boolean;
  latitude_e6: number | null;
  longitude_e6: number | null;
  distance_m: number | null;
  radius_m: number | null;
  created_label: string;
  created_at: string;
  updated_at: string;
}

export interface BrokerAddressInput {
  zip_code?: string;
  street?: string;
  number?: string;
  complement?: string;
  district?: string;
  city?: string;
  state?: string;
  city_code?: string;
  international?: boolean;
  position?: number;
  is_main?: boolean;
  active?: boolean;
}

export interface BrokerNomination {
  policyholder_id: string;
  document: string;
  legal_name: string;
  /** Data de nomeacao. Ainda nao existe no tomador, e sai nula. */
  nominated_on: string | null;
  valid_until: string | null;
  instrument: string;
  status: string;
}

export interface BrokerRequestDocumentItem {
  id: string;
  name: string;
  description: string;
  required: boolean;
  document_type_id: string | null;
  template_document_id: string | null;
  position: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface BrokerRequestDocumentItemInput {
  name?: string;
  description?: string;
  required?: boolean;
  document_type_id?: string | null;
  template_document_id?: string | null;
  position?: number;
  active?: boolean;
}

export interface BrokerPendingDocuments {
  has_pending: boolean;
  count: number;
  documents: {
    id: string;
    number: number;
    group: string;
    title: string;
    status: string;
    request_item_id: string | null;
  }[];
  /** Itens obrigatorios do catalogo ainda nao pedidos. Nao contam como pendencia. */
  not_requested: BrokerRequestDocumentItem[];
}

export interface BrokerRequestedDocument {
  id: string;
  number: number;
  title: string;
  required: boolean;
  request_item_id: string;
}

export interface BrokerChannelType {
  id: string;
  name: string;
  description: string;
  registration_only: boolean;
  requires_insurer_codes: boolean;
  position: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface BrokerChannelTypeInput {
  name?: string;
  description?: string;
  registration_only?: boolean;
  requires_insurer_codes?: boolean;
  position?: number;
  active?: boolean;
}

/** Linha do "Exportar Dados", sem conta bancaria, Pix e CPF do responsavel. */
export interface BrokerExportRow {
  number: number;
  document: string;
  legal_name: string;
  trade_name: string;
  commission_bps: number;
  max_commission_bps: number | null;
  created_at: string;
  external_status: string;
  stage: string;
  is_blocked: boolean;
  blocked_at: string | null;
  susep_code: string;
  cnpj_status: string;
  cnae_code: string;
  channel_name: string;
  level: string;
  registration_expires_on: string | null;
  email: string;
  phone: string;
  city: string;
  state: string;
}

/** Banco do catalogo da plataforma (Core/GetBanks do OnPoint). */
export interface BrokerBankCode {
  compe_code: string;
  name: string;
  status: string;
}

// --- Assistente de cadastro de corretor (P-COR-2) -------------------------------

/**
 * Resultado da consulta do CNPJ na Receita Federal, pela BrasilAPI (ADR 0092). Com a
 * consulta desligada na instalacao, e nao_configurada, e a tela segue com o
 * preenchimento manual.
 */
export type BrokerCompanyLookupStatus =
  | 'nao_configurada'
  | 'encontrada'
  | 'nao_encontrada'
  | 'indisponivel'
  | 'nao_consultada';

/** Dados cadastrais que a consulta do CNPJ devolve, para preencher o cadastro. */
export interface BrokerCompanyData {
  legal_name: string;
  trade_name: string;
  cnpj_status: string;
  cnae_code: string;
  legal_nature: string;
  email: string;
  phone: string;
  address: Address;
}

/** Resposta do "Iniciar cadastro" do passo Buscar CNPJ. */
export interface BrokerRegistrationCheck {
  /** CNPJ so com digitos. */
  document: string;
  already_registered: boolean;
  /** A corretora ja cadastrada, so para quem pode ler o cadastro. */
  broker: { id: string; number: number; legal_name: string; stage: string } | null;
  company_lookup: {
    status: BrokerCompanyLookupStatus;
    provider: string;
    message: string;
    data: BrokerCompanyData | null;
  };
  /** Triagem na SUSEP (ADR 0095). */
  susep?: BrokerSusepScreening;
}

/** Pessoa da corretora nova: o administrador do portal ou um usuario. */
export interface BrokerOnboardingPerson {
  name: string;
  email: string;
  /** Celular com DDD. Opcional. */
  phone?: string;
  /** Perfis da corretora. */
  role_ids: string[];
}

/** O assistente inteiro, enviado no "Concluir cadastro". */
export interface BrokerOnboardingInput {
  /** Os passos 2 e 3, no formato do cadastro simples. */
  broker: BrokerInput;
  portal_admin?: BrokerOnboardingPerson;
  users?: BrokerOnboardingPerson[];
}

/** Convite emitido. O endereco de aceite aparece so aqui, uma vez. */
export interface BrokerOnboardingInvitation {
  id: string;
  name: string;
  email: string;
  role_ids: string[];
  portal_admin: boolean;
  expires_at: string;
  accept_url: string;
  delivered_by_email: boolean;
}

export interface BrokerOnboardingResult {
  broker: Broker;
  invitations: BrokerOnboardingInvitation[];
}

// --- Assessoria, corretor digital e limite de consultas (P-COR-3) ---------------

/** Janela da contagem do limite de consultas, no horario de Brasilia. */
export type BrokerConsultationPeriod = 'diario' | 'mensal';

export interface BrokerConsultationLimit {
  id: string;
  max_queries: number;
  period: BrokerConsultationPeriod;
  /** Bloqueio automatico ao atingir o limite. Nasce desligado (regra sem fonte). */
  auto_block: boolean;
  active: boolean;
  notes: string;
  unblocked_at: string | null;
  unblock_reason: string;
  unblocked_label: string;
  created_label: string;
  created_at: string;
  updated_at: string;
}

/**
 * O limite de consultas de bureau e a situacao do periodo corrente. `enforced` diz se
 * o limite barra a consulta: ativo, com o bloqueio ligado e sem desbloqueio no periodo.
 */
export interface BrokerConsultationStatus {
  broker_id: string;
  configured: boolean;
  limit: BrokerConsultationLimit | null;
  period_start: string;
  period_end: string;
  used: number;
  remaining: number | null;
  reached: boolean;
  blocked: boolean;
  block_reason: '' | 'limite_atingido';
  unblocked_in_period: boolean;
  enforced: boolean;
}

/** O limite inteiro. Ausente, fica no padrao (mensal, sem bloqueio, ativo). */
export interface BrokerConsultationLimitInput {
  max_queries: number;
  period?: BrokerConsultationPeriod;
  auto_block?: boolean;
  active?: boolean;
  notes?: string;
}

/** Resultado da consulta da comissao contra o limite de comissao (COR-216). */
export interface BrokerConsultationCommissionCheck {
  broker_id: string;
  rate_bps: number;
  commission_bps: number;
  max_commission_bps: number | null;
  result: 'dentro_do_limite' | 'acima_do_limite' | 'sem_limite';
  within_limit: boolean | null;
  excess_bps: number | null;
  /** Sempre falso: a consulta so compara. */
  enforced: boolean;
  pending_rules: string[];
}

/** Assessoria: quem reune uma carteira de corretoras (CFG-003, nasce desligada). */
export interface Advisor {
  id: string;
  name: string;
  /** CNPJ so com digitos. Vazio e nao informado. */
  document: string;
  email: string;
  phone: string;
  active: boolean;
  notes: string;
  broker_count: number;
  created_label: string;
  created_at: string;
  updated_at: string;
}

export interface AdvisorInput {
  name?: string;
  document?: string;
  email?: string;
  phone?: string;
  active?: boolean;
  notes?: string;
}

/** Corretora da assessoria, com a comissao da assessoria (regra sem fonte). */
export interface AdvisorBroker {
  advisor_id: string;
  broker_id: string;
  /** Comissao da assessoria em pontos-base. Nula e nao definida. */
  commission_bps: number | null;
  broker: {
    number: number;
    legal_name: string;
    document: string;
    stage: string;
    commission_bps: number;
    max_commission_bps: number | null;
    is_digital: boolean;
  };
  created_label: string;
  created_at: string;
  updated_at: string;
}

/** Funcionalidade da assessoria, que nasce desligada em toda empresa. */
export const FUNCIONALIDADE_ASSESSORIA = 'corretores.assessoria';

export const advisors = {
  list: (params: ListParams, signal?: AbortSignal) =>
    requestPage<Advisor>(`/api/v1/advisors${query(params)}`, signal),
  get: (id: string, signal?: AbortSignal) =>
    api.get<Advisor>(`/api/v1/advisors/${id}`, signal),
  create: (input: AdvisorInput) => api.post<Advisor>('/api/v1/advisors', input),
  update: (id: string, input: AdvisorInput) =>
    api.patch<Advisor>(`/api/v1/advisors/${id}`, input),
  brokers: (id: string, signal?: AbortSignal) =>
    api.get<AdvisorBroker[]>(`/api/v1/advisors/${id}/brokers`, signal),
  addBroker: (id: string, brokerId: string, commissionBps: number | null) =>
    api.post<AdvisorBroker>(
      `/api/v1/advisors/${id}/brokers`,
      commissionBps === null
        ? { broker_id: brokerId }
        : { broker_id: brokerId, commission_bps: commissionBps },
    ),
  /** Nulo limpa a comissao da assessoria. */
  updateBroker: (id: string, brokerId: string, commissionBps: number | null) =>
    api.patch<AdvisorBroker>(`/api/v1/advisors/${id}/brokers/${brokerId}`, {
      commission_bps: commissionBps,
    }),
  removeBroker: (id: string, brokerId: string) =>
    api.delete<void>(`/api/v1/advisors/${id}/brokers/${brokerId}`),
};

/** Teto da lista inteira que o quadro carrega, em paginas de 100. */
const tetoDoQuadro = 2000;

export const brokers = {
  list: (params: ListParams, signal?: AbortSignal) =>
    requestPage<Broker>(`/api/v1/brokers${query(params)}`, signal),
  /**
   * A lista inteira do filtro, em paginas de 100, ate o teto do quadro. O servidor
   * devolve no maximo 100 por pagina: o quadro que pedia 200 recebia 100 e escondia
   * o resto (defeito 4 de "Corretora, tomador..." do anexo C do 47).
   */
  listAll: async (params: ListParams, signal?: AbortSignal) => {
    const itens: Broker[] = [];
    let total = 0;
    for (let offset = 0; offset < tetoDoQuadro; offset += 100) {
      const pagina = await brokers.list({ ...params, limit: 100, offset }, signal);
      itens.push(...pagina.items);
      total = pagina.total;
      if (pagina.items.length === 0 || itens.length >= pagina.total) break;
    }
    return { items: itens, total };
  },
  get: (id: string, signal?: AbortSignal) =>
    api.get<Broker>(`/api/v1/brokers/${id}`, signal),
  /** A corretora da sessao (portal da corretora). */
  current: (signal?: AbortSignal) => api.get<Broker>('/api/v1/brokers/current', signal),
  create: (input: BrokerInput) => api.post<Broker>('/api/v1/brokers', input),
  update: (id: string, input: BrokerInput) =>
    api.patch<Broker>(`/api/v1/brokers/${id}`, input),
  transition: (id: string, stage: string, blockReason?: string) =>
    api.post<Broker>(
      `/api/v1/brokers/${id}/transitions`,
      blockReason === undefined ? { stage } : { stage, block_reason: blockReason },
    ),
  remove: (id: string) => api.delete<void>(`/api/v1/brokers/${id}`),
  /** Lista inteira do filtro, para o "Exportar dados". Exige a permissao de exportar. */
  export: (params: ListParams) =>
    api.get<BrokerExportRow[]>(`/api/v1/brokers/export${query(params)}`),

  partners: (id: string, signal?: AbortSignal) =>
    api.get<BrokerPartner[]>(`/api/v1/brokers/${id}/partners`, signal),
  addPartner: (id: string, input: BrokerPartnerInput) =>
    api.post<BrokerPartner>(`/api/v1/brokers/${id}/partners`, input),
  updatePartner: (id: string, partnerId: string, input: BrokerPartnerInput) =>
    api.patch<BrokerPartner>(`/api/v1/brokers/${id}/partners/${partnerId}`, input),
  removePartner: (id: string, partnerId: string) =>
    api.delete<void>(`/api/v1/brokers/${id}/partners/${partnerId}`),

  bankAccounts: (id: string, signal?: AbortSignal) =>
    api.get<BrokerBankAccountEntry[]>(`/api/v1/brokers/${id}/bank-accounts`, signal),

  responsibles: (id: string, signal?: AbortSignal) =>
    api.get<BrokerResponsible[]>(`/api/v1/brokers/${id}/responsibles`, signal),
  addResponsible: (id: string, input: BrokerResponsibleInput) =>
    api.post<BrokerResponsible>(`/api/v1/brokers/${id}/responsibles`, input),
  updateResponsible: (
    id: string,
    responsibleId: string,
    input: BrokerResponsibleInput,
  ) =>
    api.patch<BrokerResponsible>(
      `/api/v1/brokers/${id}/responsibles/${responsibleId}`,
      input,
    ),
  removeResponsible: (id: string, responsibleId: string) =>
    api.delete<void>(`/api/v1/brokers/${id}/responsibles/${responsibleId}`),

  addresses: (id: string, signal?: AbortSignal) =>
    api.get<BrokerAddress[]>(`/api/v1/brokers/${id}/addresses`, signal),
  addAddress: (id: string, input: BrokerAddressInput) =>
    api.post<BrokerAddress>(`/api/v1/brokers/${id}/addresses`, input),
  updateAddress: (id: string, addressId: string, input: BrokerAddressInput) =>
    api.patch<BrokerAddress>(`/api/v1/brokers/${id}/addresses/${addressId}`, input),
  removeAddress: (id: string, addressId: string) =>
    api.delete<void>(`/api/v1/brokers/${id}/addresses/${addressId}`),

  nominations: (id: string, signal?: AbortSignal) =>
    api.get<BrokerNomination[]>(`/api/v1/brokers/${id}/nominations`, signal),
  pendingDocuments: (id: string, signal?: AbortSignal) =>
    api.get<BrokerPendingDocuments>(`/api/v1/brokers/${id}/pending-documents`, signal),
  requestDocuments: (id: string, itemIds: string[]) =>
    api.post<BrokerRequestedDocument[]>(`/api/v1/brokers/${id}/requested-documents`, {
      item_ids: itemIds,
    }),

  channelTypes: (params: { active?: boolean } = {}, signal?: AbortSignal) =>
    api.get<BrokerChannelType[]>(
      `/api/v1/broker-channel-types${query({ active: params.active })}`,
      signal,
    ),
  createChannelType: (input: BrokerChannelTypeInput) =>
    api.post<BrokerChannelType>('/api/v1/broker-channel-types', input),
  updateChannelType: (channelId: string, input: BrokerChannelTypeInput) =>
    api.patch<BrokerChannelType>(`/api/v1/broker-channel-types/${channelId}`, input),

  requestDocumentItems: (params: { active?: boolean } = {}, signal?: AbortSignal) =>
    api.get<BrokerRequestDocumentItem[]>(
      `/api/v1/settings/broker-request-documents${query({ active: params.active })}`,
      signal,
    ),
  createRequestDocumentItem: (input: BrokerRequestDocumentItemInput) =>
    api.post<BrokerRequestDocumentItem>(
      '/api/v1/settings/broker-request-documents',
      input,
    ),
  updateRequestDocumentItem: (itemId: string, input: BrokerRequestDocumentItemInput) =>
    api.patch<BrokerRequestDocumentItem>(
      `/api/v1/settings/broker-request-documents/${itemId}`,
      input,
    ),

  /** Passo Buscar CNPJ do assistente: o CNPJ ja cadastrado e a consulta a Receita. */
  registrationCheck: (document: string, signal?: AbortSignal) =>
    api.get<BrokerRegistrationCheck>(
      `/api/v1/brokers/registration-check${query({ document })}`,
      signal,
    ),
  /** "Concluir cadastro": corretora, conta, responsavel e convites numa transacao. */
  onboard: (input: BrokerOnboardingInput) =>
    api.post<BrokerOnboardingResult>('/api/v1/brokers/onboarding', input),

  // --- P-COR-3 -----------------------------------------------------------------
  /** Limite de consultas de bureau e a situacao do periodo (com o "Atencao!"). */
  consultationLimit: (id: string, signal?: AbortSignal) =>
    api.get<BrokerConsultationStatus>(
      `/api/v1/brokers/${id}/consultation-limit`,
      signal,
    ),
  createConsultationLimit: (id: string, input: BrokerConsultationLimitInput) =>
    api.post<BrokerConsultationStatus>(
      `/api/v1/brokers/${id}/consultation-limit`,
      input,
    ),
  replaceConsultationLimit: (id: string, input: BrokerConsultationLimitInput) =>
    api.put<BrokerConsultationStatus>(
      `/api/v1/brokers/${id}/consultation-limit`,
      input,
    ),
  unblockConsultations: (id: string, reason: string) =>
    api.post<BrokerConsultationStatus>(
      `/api/v1/brokers/${id}/consultation-limit/unblock`,
      { reason },
    ),
  /** Ativa ou desativa o corretor digital. So a marca muda. */
  setDigital: (id: string, active: boolean) =>
    api.post<Broker>(`/api/v1/brokers/${id}/digital-activation`, { active }),
  /** Compara a taxa com o limite de comissao. So informa; nada e bloqueado. */
  commissionValidation: (id: string, rateBps: number, signal?: AbortSignal) =>
    api.get<BrokerConsultationCommissionCheck>(
      `/api/v1/brokers/${id}/commission-validation${query({ rate_bps: rateBps })}`,
      signal,
    ),

  /** Bancos ativos do catalogo da plataforma, para a escolha da conta. */
  bankCodes: async (signal?: AbortSignal) => {
    const pagina = await requestPage<BrokerBankCode>(
      '/api/v1/catalogs/bank-codes?status=ativo&limit=1000&sort=compe_code',
      signal,
    );
    return pagina.items;
  },
};

// --- Acesso da assessoria e autocadastro da corretora (P-ACE-2) ----------------

/** Pessoa ligada a assessoria, que entra pela tela de acesso da assessoria. */
export interface AdvisorMember {
  member_id: string;
  user_id: string;
  name: string;
  email: string;
  status: 'active' | 'inactive' | 'suspended';
  /** IsAssessoriaMaster do OnPoint. So a marca: o efeito nao tem fonte. */
  master: boolean;
  updated_at: string;
}

export const advisorMembers = {
  list: (advisorId: string, signal?: AbortSignal) =>
    api.get<AdvisorMember[]>(`/api/v1/advisors/${advisorId}/members`, signal),
  link: (advisorId: string, memberId: string, master: boolean) =>
    api.put<AdvisorMember>(`/api/v1/advisors/${advisorId}/members/${memberId}`, {
      master,
    }),
  unlink: (advisorId: string, memberId: string) =>
    api.delete<void>(`/api/v1/advisors/${advisorId}/members/${memberId}`),
  /**
   * Pessoas que a ligacao aceitaria (sem corretora, sem assessoria e so com perfis da
   * corretora), pelo nome ou e-mail. A busca e no servidor (R5-B22).
   */
  candidates: (
    advisorId: string,
    params: { q?: string; limit?: number },
    signal?: AbortSignal,
  ) =>
    api.get<AdvisorMember[]>(
      `/api/v1/advisors/${advisorId}/member-candidates${query(params)}`,
      signal,
    ),
};

export type BrokerSelfSignupStatus =
  | 'pendente_confirmacao'
  | 'em_preenchimento'
  | 'em_analise'
  | 'aprovado'
  | 'reprovado'
  | 'cancelado';

/** Resposta do inicio e da conclusao do autocadastro. */
export interface BrokerSelfSignupReceipt {
  registration_id: string;
  status: BrokerSelfSignupStatus;
  /** Para onde o link foi, sem o endereco inteiro. */
  email_masked: string;
  expires_at: string | null;
  email_delivery_available: boolean;
  message: string;
}

/** O pedido aberto pelo link, com as listas do assistente. */
export interface BrokerSelfSignupPublic {
  registration_id: string;
  status: BrokerSelfSignupStatus;
  document: string;
  email_masked: string;
  expires_at: string | null;
  tenant_slug: string;
  tenant_name: string;
  draft: Partial<BrokerOnboardingInput>;
  options: {
    channels: BrokerChannelType[];
    roles: { id: string; key: string; name: string; is_default: boolean }[];
    banks: { code: string; name: string }[];
  };
}

/** O pedido visto pela seguradora. O rascunho sai so no detalhe e ate a decisao. */
export interface BrokerSelfSignup {
  id: string;
  status: BrokerSelfSignupStatus;
  document: string;
  legal_name: string;
  contact_email: string;
  email_confirmed_at: string | null;
  links_sent: number;
  legal_basis: string;
  privacy_notice_version: string;
  privacy_acknowledged_at: string;
  submitted_at: string | null;
  decided_at: string | null;
  decided_label: string;
  decision_note: string;
  broker_id: string | null;
  personal_data_erased_at: string | null;
  /**
   * `cadastro_publico` e o cadastro publico (ADR 0095), que ja nasce aprovado; o acesso
   * vale depois da ativacao pelo e-mail (ADR 0096), registrada em `email_confirmed_at`.
   */
  channel?: 'link_por_email' | 'cadastro_publico';
  draft?: Partial<BrokerOnboardingInput>;
  created_at: string;
  updated_at: string;
}

export interface BrokerSelfSignupApproval {
  registration: BrokerSelfSignup;
  broker: Broker;
  invitations: BrokerOnboardingInvitation[];
}

/**
 * Autocadastro publico da corretora: as rotas publicas (sem sessao, com o segredo do
 * link no corpo) e as da seguradora que decide.
 */
export const brokerSelfSignup = {
  start: (input: {
    tenant_slug?: string;
    domain?: string;
    document: string;
    contact_email: string;
    privacy_acknowledged: boolean;
  }) =>
    api.post<BrokerSelfSignupReceipt>('/api/v1/public/broker-registrations', input, {
      skipAuth: true,
    }),
  confirm: (registrationId: string, token: string) =>
    api.post<BrokerSelfSignupPublic>(
      `/api/v1/public/broker-registrations/${registrationId}/confirmation`,
      { token },
      { skipAuth: true },
    ),
  saveDraft: (registrationId: string, token: string, draft: BrokerOnboardingInput) =>
    api.put<{ registration_id: string; status: BrokerSelfSignupStatus }>(
      `/api/v1/public/broker-registrations/${registrationId}`,
      { token, draft },
    ),
  complete: (registrationId: string, token: string, draft: BrokerOnboardingInput) =>
    api.post<BrokerSelfSignupReceipt>(
      `/api/v1/public/broker-registrations/${registrationId}/completion`,
      { token, draft },
      { skipAuth: true },
    ),

  list: (
    params: ListParams & { status?: BrokerSelfSignupStatus },
    signal?: AbortSignal,
  ) =>
    requestPage<BrokerSelfSignup>(
      `/api/v1/broker-registrations${query(params)}`,
      signal,
    ),
  get: (registrationId: string, signal?: AbortSignal) =>
    api.get<BrokerSelfSignup>(`/api/v1/broker-registrations/${registrationId}`, signal),
  approve: (registrationId: string, note: string) =>
    api.post<BrokerSelfSignupApproval>(
      `/api/v1/broker-registrations/${registrationId}/approval`,
      { note },
    ),
  reject: (registrationId: string, reason: string) =>
    api.post<BrokerSelfSignup>(
      `/api/v1/broker-registrations/${registrationId}/rejection`,
      {
        reason,
      },
    ),
};

/** Permissoes do autocadastro (P-ACE-2). */
export const PERMISSAO_AUTOCADASTRO_VER = 'corretores.autocadastro.view';
export const PERMISSAO_AUTOCADASTRO_APROVAR = 'corretores.autocadastro.approve';

/**
 * A aprovacao cria a corretora e convida as pessoas pelos caminhos do assistente da
 * seguradora, e por isso pede tambem as permissoes deles (R5-M13): cadastrar corretor,
 * convidar e conceder perfil. A conta bancaria informada pede ainda a de dados
 * bancarios.
 */
export const PERMISSOES_DA_APROVACAO_DO_AUTOCADASTRO = [
  PERMISSAO_AUTOCADASTRO_APROVAR,
  'corretores.corretor.create',
  'usuarios.usuario.create',
  'usuarios.papel.update',
] as const;
export const PERMISSAO_DADOS_BANCARIOS = 'corretores.dados_bancarios.update';
